/**
 * EAA-1839: the two pure translations from this screen's UI state to
 * `GET /organizations/{id}/accounts` query params.
 *
 * Extracted so the list query (`useScopedAccounts`), the facet counts
 * (`useScopedAccountFacets`) and the "select all matching" page walk all build
 * their params through the SAME function. They must agree exactly: a facet count
 * computed from a slightly different filter set than the list it labels would put
 * a wrong number beside every option, and a select-all walk that drifted from the
 * list would tick rows the operator never saw.
 */

import type { ScopedAccountsParams } from '@/api/orgAdmin';
import type {
    FacetGroupKey,
    ScopedAccountSortField,
    ScopedAccountsFilters,
    SortDirection,
} from './types';

/** Only `granted_at` / `user_sub` / `sync_status` are accepted server-side. */
export function orderingParam(field: ScopedAccountSortField, direction: SortDirection): string {
    return direction === 'desc' ? `-${field}` : field;
}

/**
 * Every server filter currently set, optionally EXCLUDING the one a given facet
 * group owns.
 *
 * `omit` is what makes the sidebar counts mean "how many rows would I get if I
 * also ticked this option" rather than "how many rows match, including the option
 * from this group that is already ticked" — the latter would show 0 beside every
 * unticked option in a group as soon as one was chosen.
 */
export function filterParams(
    filters: ScopedAccountsFilters,
    omit?: FacetGroupKey
): Omit<ScopedAccountsParams, 'page' | 'pageSize' | 'ordering'> {
    return {
        includeChildren: filters.includeChildren,
        ...(filters.issue && omit !== 'issue' ? { issue: filters.issue } : {}),
        ...(filters.levelId && omit !== 'level' ? { levelId: filters.levelId } : {}),
        ...(filters.childOrgId && omit !== 'childOrg'
            ? { childOrganizationId: filters.childOrgId }
            : {}),
        ...(filters.isActive !== null && omit !== 'status' ? { isActive: filters.isActive } : {}),
    };
}
